import { flow, Instance, isAlive, SnapshotIn, SnapshotOut, types } from 'mobx-state-tree';
import {
  CanvasBorderType,
  FilterMiniature,
  FiltersMiniatures,
  IImageEditor,
  ImageBorders,
  ImageTransformation,
  RestoreOptions,
} from '@photosi/expo-photosi-sdk/build/ImageEditor';
import { PickedImage, ThumbnailSize } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { QualityAlertSuggestions } from '@photosi/expo-photosi-sdk/build/UserImage';
import Datadog from '../../_shared/datadog';
import { DATADOG_CUSTOM_ACTIONS } from '../../_shared/datadog/DatadogCustomActions';
import { logSDKError } from 'src/_shared/logger';
import { IReactionDisposer, reaction } from 'mobx';
import { BaseModel } from './_base/baseModel';
import { IThumbnailImageModel, ThumbnailImageModel } from './thumbnailImage';
import { Orientation } from '@photosi/expo-photosi-sdk/build/UserImage';
import { IProductModel } from './product';

export type TUserImageModel = typeof UserImageModel;
export type UserImageModelInstance = Instance<TUserImageModel>;
export interface IUserImageModelIn extends SnapshotIn<TUserImageModel> {}
export interface IUserImageModelOut extends SnapshotOut<TUserImageModel> {}
export interface IUserImageModel extends UserImageModelInstance {}

export const UserImageModel = BaseModel.named('UserImage')
  .props({
    id: types.identifier,
    productId: types.string,
    userImageAreaId: types.string,
    originalImageOriginalId: types.string,

    thumbnailImage: types.maybe(types.safeReference(ThumbnailImageModel)),

    imageAreaWidth: types.optional(types.number, 0),
    imageAreaHeight: types.optional(types.number, 0),

    qualityAlert: types.optional(types.boolean, false),
    cropAlert: types.optional(types.boolean, false),

    url: types.optional(types.string, ''), // TODO: azione che quando cambia url ricarica i filtri e quality
    width: types.optional(types.number, 0),
    height: types.optional(types.number, 0),
    orientation: types.maybeNull(types.enumeration(Object.values(Orientation))),

    borderType: types.maybeNull(types.enumeration(Object.values(CanvasBorderType))),
    filter: types.optional(types.string, 'original'),
    scale: types.optional(types.number, 1),
    minScale: types.optional(types.number, 1),
    minRelativeScale: types.optional(types.number, 1),
    maxScale: types.optional(types.number, 1),
    rotationDeg: types.optional(types.number, 0),
    offsetX: types.optional(types.number, 0),
    offsetY: types.optional(types.number, 0),

    backgroundColor: types.optional(types.string, '#FFFFFF'),

    // adjustments
    brightness: types.optional(types.number, 0),
    contrast: types.optional(types.number, 0),
    saturation: types.optional(types.number, 0),

    // transformations utils
    userOffsetXTemp: types.maybe(types.number),
    userOffsetYTemp: types.maybe(types.number),

    unsavedChanges: types.optional(types.boolean, false),
    canUndo: types.optional(types.boolean, false),
    canRedo: types.optional(types.boolean, false),
    canRestore: types.optional(types.boolean, false),
    isUserEdited: types.optional(types.boolean, false),
  })
  .volatile<{
    isEditing: boolean;
    isMooving: boolean;
    isUpdating: boolean;
    sdkImageEditor?: IImageEditor;
    filtersMiniatures: FilterMiniature[];
    qualitySuggestions?: QualityAlertSuggestions;
    canvasBorders?: ImageBorders;
    allCanvasBorders: {
      [CanvasBorderType.EXTENDED]?: ImageBorders;
      [CanvasBorderType.MIRRORED]?: ImageBorders;
    };
  }>(self => ({
    isEditing: false,
    isMooving: false,
    isUpdating: false,
    filtersMiniatures: [],
    qualitySuggestions: undefined,
    canvasBorders: undefined,
    allCanvasBorders: {
      [CanvasBorderType.EXTENDED]: undefined,
      [CanvasBorderType.MIRRORED]: undefined,
    },
  }))
  .views(self => ({
    get product(): IProductModel | undefined {
      return self.root.project.activeProject?.products?.find(product => product.id === self.productId);
    },
  }))
  .actions(self => {
    const setImageAreaSizes = (sizes: { width: number; height: number }) => {
      self.imageAreaWidth = sizes.width;
      self.imageAreaHeight = sizes.height;
    };
    const setThumbnailImage = (thumbnailImage?: IThumbnailImageModel) => {
      self.thumbnailImage = thumbnailImage;
    };

    // SDK INTERACTION ACTIONS
    const initEditor = flow(function* initEditor(autosave = true) {
      try {
        const editor = yield self.sdkProject.getImageEditor(self.id, autosave);
        return editor;
      } catch (error) {
        logSDKError(error, { userImageId: self.id });
        throw error;
      }
    });
    /**
     * Initialize sdkImageEditor if not already loaded
     */
    const persistEditor = flow(function* persistEditor(autosave = true) {
      if (!self.sdkImageEditor) {
        self.sdkImageEditor = yield initEditor(autosave);
        yield loadAll();
      }
    });

    const getSdkEditorInstance: () => Promise<IImageEditor> = flow(function* getSdkEditorInstance(): Generator<
      Promise<IImageEditor>
    > {
      if (self.sdkImageEditor) {
        return self.sdkImageEditor;
      } else {
        return yield initEditor();
      }
    });

    const unloadEditor = flow(function* unloadEditor() {
      if (self.sdkImageEditor) {
        yield self.sdkImageEditor.unload();
        self.sdkImageEditor = undefined;
      }
    });

    const refreshEditor = flow(function* refreshEditor() {
      yield unloadEditor();
      if (!isAlive(self)) return;
      yield initEditor();
    });

    const loadAll = flow(function* loadAll() {
      yield Promise.all([loadFiltersMiniatures(), loadActionsPermissions()]);
    });

    const loadFiltersMiniatures = flow(function* loadFiltersMiniatures() {
      const sdkEditorInstance = yield getSdkEditorInstance();
      const filtersMiniatures: FiltersMiniatures = yield sdkEditorInstance.getFiltersMiniatures(ThumbnailSize.XS);
      self.filtersMiniatures = filtersMiniatures.miniatures ? filtersMiniatures.miniatures.reverse() : [];
    });

    const loadQualitySuggestions = flow(function* loadQualitySuggestions() {
      if (!self.qualityAlert) return;
      try {
        if (self.sdkImageEditor) {
          const sdkEditorInstance = yield getSdkEditorInstance();
          self.qualitySuggestions = yield sdkEditorInstance.getQualityAlertSuggestions();
        } else if (self.sdkProject) {
          // fallback flow
          self.qualitySuggestions = yield self.sdkProject.getQualityAlertSuggestions(self.id);
        }
      } catch (error) {
        logSDKError(error, { userImageId: self.id });
      }
    });

    const loadActionsPermissions = flow(function* loadActionsPermissions() {
      const sdkEditorInstance = yield getSdkEditorInstance();
      [self.canUndo, self.canRedo, self.canRestore] = yield Promise.all([
        sdkEditorInstance.canUndo(),
        sdkEditorInstance.canRedo(),
        sdkEditorInstance.canRestore(),
      ]);
    });

    const loadCanvasBorders = flow(function* loadCanvasBorders() {
      if (!self.borderType) return;
      yield loadAllCanvasBorders();

      if (self.borderType === CanvasBorderType.MIRRORED) {
        self.canvasBorders = self.allCanvasBorders[CanvasBorderType.MIRRORED];
      } else if (self.borderType === CanvasBorderType.EXTENDED) {
        self.canvasBorders = self.allCanvasBorders[CanvasBorderType.EXTENDED];
      } else {
        self.canvasBorders = undefined;
      }
    });

    const loadAllCanvasBorders = flow(function* loadAllCanvasBorders() {
      if (!self.borderType) return;
      const sdkEditorInstance = yield getSdkEditorInstance();
      // set borders
      self.allCanvasBorders[CanvasBorderType.EXTENDED] = yield sdkEditorInstance.generateCanvasBorders(
        CanvasBorderType.EXTENDED,
      );
      self.allCanvasBorders[CanvasBorderType.MIRRORED] = yield sdkEditorInstance.generateCanvasBorders(
        CanvasBorderType.MIRRORED,
      );
    });

    /**
     * FE -> SDK TRANSFORMATIONS
     */
    const toSdkTransformation = (): ImageTransformation => {
      return {
        relativeCropWidth: self.imageAreaWidth / (self.width * self.scale),
        relativeCropHeight: self.imageAreaHeight / (self.height * self.scale),
        relativeCropX: self.offsetX * -1,
        relativeCropY: self.offsetY * -1,
        rotationDeg: self.rotationDeg,
        scale: self.scale,
      };
    };

    /**
     * SDK TRANSFORMATIONS -> FE
     * @param sdkImageTransformation
     */
    const sdkTransformationParser = (sdkImageTransformation: ImageTransformation): void => {
      self.offsetX = sdkImageTransformation.relativeCropX * -1;
      self.offsetY = sdkImageTransformation.relativeCropY * -1;
      self.rotationDeg = sdkImageTransformation.rotationDeg;
      self.scale = sdkImageTransformation.scale;
    };

    /**
     * VALIDATE TRANSFORMATIONS
     */
    const validateTransformations = flow(function* validateTransformations(transformation: ImageTransformation) {
      const sdkEditorInstance: IImageEditor = yield getSdkEditorInstance();
      // validate transformations
      const validTransformations: ImageTransformation = yield sdkEditorInstance.validateTransformations(transformation);
      return validTransformations;
    });

    /**
     * SUBMITS, VALIDATE and APPLY TRANSFORMATIONS
     */
    const setTransformations = flow(function* setTransformations() {
      const sdkEditorInstance = yield getSdkEditorInstance();
      // parse transformation
      const skdTransformations: ImageTransformation = toSdkTransformation();
      // validate transformations and auto adjust transformations
      yield sdkEditorInstance.setTransformations(skdTransformations);

      yield loadActionsPermissions();
    });

    const setFilter = flow(function* setFilter(value: string) {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.setFilter(value);
      yield loadActionsPermissions();
    });

    const setBorder = flow(function* setBorder(value: CanvasBorderType) {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.setBorderType(value);

      // @ts-ignore
      self.canvasBorders = self.allCanvasBorders[value];

      yield loadActionsPermissions();
    });

    const setBackgroundColor = flow(function* setBackgroundColor(value: string) {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.setBackgroundColor(value);
      yield loadActionsPermissions();
    });

    const setAdjustments = flow(function* setAdjustments(values) {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.setAdjustments(values);
      yield loadActionsPermissions();
    });

    const setOrientation = flow(function* setOrientation(value: Orientation) {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.setOrientation(value);
      yield loadActionsPermissions();
    });

    const flipHorizontal = flow(function* flipHorizontal() {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.flipHorizontal();
      yield loadActionsPermissions();
    });

    const flipVertical = flow(function* flipVertical() {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.flipVertical();
      yield loadActionsPermissions();
    });

    const generateAdjustedImage = flow(function* applyAdjustments(values) {
      const sdkEditorInstance = yield getSdkEditorInstance();
      const newUrl = yield sdkEditorInstance.generateAdjustedThumbnail(values);
      self.url = newUrl;
    });

    const cancel = flow(function* cancel() {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.cancel();
      yield loadActionsPermissions();
    });

    const restore = flow(function* restore(options: RestoreOptions = { keepFilter: false, keepAdjustments: false }) {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.restore(options);
      yield loadFiltersMiniatures();
      yield loadActionsPermissions();
    });

    const replace = flow(function* save(file: PickedImage) {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.replaceImage(file);
      yield loadFiltersMiniatures();
      yield loadActionsPermissions();
    });

    const undo = flow(function* undo() {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.undo();
      yield loadFiltersMiniatures();
      yield loadActionsPermissions();
    });

    const redo = flow(function* redo() {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.redo();
      yield loadFiltersMiniatures();
      yield loadActionsPermissions();
    });

    /*+
     * @deprecated
     */
    const getQualityAlertSuggestions = flow(function* getQualityAlertSuggestions() {
      if (!self.sdkProject) return;
      return yield self.sdkProject.getQualityAlertSuggestions(self.id);
    });

    const saveChanges = flow(function* saveChanges() {
      const sdkEditorInstance = yield getSdkEditorInstance();
      yield sdkEditorInstance.saveChanges();
    });

    return {
      setImageAreaSizes,
      setThumbnailImage,
      initEditor,
      persistEditor,
      unloadEditor,
      refreshEditor,
      loadAll,
      loadFiltersMiniatures,
      loadQualitySuggestions,
      loadCanvasBorders,
      sdkTransformationParser,
      toSdkTransformation,
      validateTransformations,
      setTransformations,
      setFilter,
      setBorder,
      setBackgroundColor,
      setAdjustments,
      setOrientation,
      flipHorizontal,
      flipVertical,
      generateAdjustedImage,
      cancel,
      restore,
      replace,
      undo,
      redo,
      getQualityAlertSuggestions,
      saveChanges,
    };
  })
  .actions(self => ({
    setIsUpdating(isUpdating: boolean) {
      self.isUpdating = isUpdating;
    },
  }))
  .actions(self => {
    let reactions: IReactionDisposer[] = [];

    return {
      afterCreate() {
        // Init reactions
        const canvasBorderReaction = reaction(
          () => ({
            borderType: self.borderType,
            offsetX: self.offsetX,
            offsetY: self.offsetY,
            filter: self.filter,
            scale: self.scale,
            rotationDeg: self.rotationDeg,
            brightness: self.brightness,
            contrast: self.contrast,
            saturation: self.saturation,
          }),
          newValue => {
            if (self.isEditing) return;
            self.awaitSdkProject().then(() => self.loadCanvasBorders());
          },
          { name: 'canvasBorderReaction', fireImmediately: true, delay: 100 },
        );
        reactions.push(canvasBorderReaction);

        const qualityAlertReaction = reaction(
          () => ({
            qualityAlert: self.qualityAlert,
          }),
          newValue => {
            if (newValue) self.awaitSdkProject().then(() => self.loadQualitySuggestions());
          },
          { name: 'qualityAlertReaction', fireImmediately: true, delay: 100 },
        );
        reactions.push(qualityAlertReaction);
      },

      beforeDestroy() {
        // Clean up safeReference before destruction
        self.thumbnailImage = undefined;

        // Remove all reactions before destruction
        reactions.forEach(dispose => dispose());
        reactions = [];
        if (__DEV__) console.log('[UserImageModel] references cleaned and reactions removed before destroy');
      },
    };
  })
  .actions(self => {
    // EDITOR ACTIONS
    const select = (autosave = true) => {
      // initialize editor instance
      return self.persistEditor(autosave);
    };

    const deselect = () => {
      return self.unloadEditor();
    };

    const setOffsets = (value: { x: number; y: number }): void => {
      self.userOffsetXTemp = value.x;
      self.userOffsetYTemp = value.y;
      self.isEditing = true;
      self.isMooving = true;
    };

    const applyOffsets = (value: { x: number; y: number }, responsiveScale: number): void => {
      self.userOffsetXTemp = undefined;
      self.userOffsetYTemp = undefined;

      self.offsetX = self.offsetX + value.x / (self.width * self.scale * responsiveScale);
      self.offsetY = self.offsetY + value.y / (self.height * self.scale * responsiveScale);

      self.isEditing = false;
      self.isMooving = false;
      self.setTransformations();
    };

    const calculateNewScaleAndOffsets = (newScale: number, responsiveScale: number) => {
      const scaleFactor = newScale / self.scale;

      const currentUserPhotoDimension = {
        width: self.width * self.scale * responsiveScale,
        height: self.height * self.scale * responsiveScale,
      };

      const currentUserPhotoPosition = {
        x: self.offsetX * currentUserPhotoDimension.width,
        y: self.offsetY * currentUserPhotoDimension.height,
      };

      const newUserPhotoDimension = {
        width: self.width * newScale * responsiveScale,
        height: self.height * newScale * responsiveScale,
      };

      const pivot = {
        x: (self.imageAreaWidth * responsiveScale) / 2,
        y: (self.imageAreaHeight * responsiveScale) / 2,
      };

      // Aggiorniamo la posizione in modo che il punto pivot rimanga fisso
      const newUserPhotoPosition = {
        x: pivot.x + scaleFactor * (currentUserPhotoPosition.x - pivot.x),
        y: pivot.y + scaleFactor * (currentUserPhotoPosition.y - pivot.y),
      };

      return {
        scale: newScale,
        offsetY: newUserPhotoPosition.y / newUserPhotoDimension.height,
        offsetX: newUserPhotoPosition.x / newUserPhotoDimension.width,
      };
    };

    const setScale = (newScale: number, responsiveScale: number): void => {
      const { offsetX, offsetY, scale } = calculateNewScaleAndOffsets(newScale, responsiveScale);

      self.scale = scale;
      self.offsetX = offsetX;
      self.offsetY = offsetY;

      self.isEditing = true;
    };

    const applyScale = flow(function* applyScale(newScale: number, responsiveScale: number) {
      const { offsetX, offsetY, scale } = calculateNewScaleAndOffsets(newScale, responsiveScale);

      self.scale = scale;
      self.offsetX = offsetX;
      self.offsetY = offsetY;

      self.setTransformations();
      self.isEditing = false;
    });

    const applyStepScale = flow(function* applyScaleNew(newScale: number, responsiveScale: number) {
      const { offsetX, offsetY, scale } = calculateNewScaleAndOffsets(newScale, responsiveScale);

      // parse transformations to sdk transformations
      const newTransofrmations: ImageTransformation = {
        rotationDeg: self.rotationDeg,
        scale,
        relativeCropX: offsetX * -1,
        relativeCropY: offsetY * -1,
        relativeCropWidth: self.imageAreaWidth / (self.width * scale),
        relativeCropHeight: self.imageAreaHeight / (self.height * scale),
      };
      // validate new transformations
      const validTransformations: ImageTransformation = yield self.validateTransformations(newTransofrmations);
      // parse and apply valid transformations back
      self.scale = validTransformations.scale;
      self.offsetX = validTransformations.relativeCropX * -1;
      self.offsetY = validTransformations.relativeCropY * -1;

      self.setTransformations();
      self.isEditing = false;
    });

    const calculateNewRotation = (newRotationDeg: number, responsiveScale: number) => {
      const deltaDeg = newRotationDeg - self.rotationDeg;
      const delta = (deltaDeg * Math.PI) / 180; // conversione in radianti
      const newRotation = self.rotationDeg + deltaDeg;

      const currentUserPhotoDimension = {
        width: self.width * self.scale * responsiveScale,
        height: self.height * self.scale * responsiveScale,
      };

      const currentUserPhotoPosition = {
        x: self.offsetX * currentUserPhotoDimension.width,
        y: self.offsetY * currentUserPhotoDimension.height,
      };

      // Calcola il centro attuale dell'immagine
      const imageCenter = {
        x: currentUserPhotoPosition.x + currentUserPhotoDimension.width / 2,
        y: currentUserPhotoPosition.y + currentUserPhotoDimension.height / 2,
      };

      // Definisci il pivot come il centro della griglia
      const pivot = {
        x: (self.imageAreaWidth * responsiveScale) / 2,
        y: (self.imageAreaHeight * responsiveScale) / 2,
      };

      const cos = Math.cos(delta);
      const sin = Math.sin(delta);

      // Calcola il nuovo centro dell'immagine affinché il punto sotto il pivot rimanga invariato
      const newImageCenter = {
        x: pivot.x - (cos * (pivot.x - imageCenter.x) - sin * (pivot.y - imageCenter.y)),
        y: pivot.y - (sin * (pivot.x - imageCenter.x) + cos * (pivot.y - imageCenter.y)),
      };

      // La nuova posizione dell'immagine (angolo in alto a sinistra)
      const newUserPhotoPosition = {
        x: newImageCenter.x - currentUserPhotoDimension.width / 2,
        y: newImageCenter.y - currentUserPhotoDimension.height / 2,
      };

      return {
        rotationDeg: newRotation,
        offsetY: newUserPhotoPosition.y / currentUserPhotoDimension.height,
        offsetX: newUserPhotoPosition.x / currentUserPhotoDimension.width,
      };
    };

    const setRotation = (value: number, responsiveScale: number): void => {
      const { rotationDeg, offsetX, offsetY } = calculateNewRotation(value, responsiveScale);

      self.rotationDeg = rotationDeg;
      self.offsetX = offsetX;
      self.offsetY = offsetY;
      self.isEditing = true;
    };

    const applyRotation = (value: number, responsiveScale: number): void => {
      const { rotationDeg, offsetX, offsetY } = calculateNewRotation(value, responsiveScale);

      self.rotationDeg = rotationDeg;
      self.offsetX = offsetX;
      self.offsetY = offsetY;

      self.isEditing = false;
      self.setTransformations();
    };

    const replaceImage = flow(function* (param: { files: PickedImage[] }) {
      // add opacity during updating userImage
      self.isUpdating = true;
      self.root.dynamicModal.activeModal?.setIsUploadingPhoto(true);
      yield self.replace(param.files[0]);
    });

    const applyFilter = (value: string): void => {
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.filter_apply_time);
      // add opacity during updating userImage
      self.isUpdating = true;
      self.setFilter(value);
    };

    const applyBorderType = (value: CanvasBorderType): void => {
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.border_type_apply_time);
      // add opacity during updating userImage
      self.isUpdating = true;
      self.setBorder(value);
    };

    const applyOrientation = (value: Orientation): void => {
      // Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.orientation_apply_time);
      // add opacity during updating userImage
      self.isUpdating = true;
      self.setOrientation(value);
    };

    return {
      select,
      deselect,
      replaceImage,
      setOffsets,
      applyOffsets,
      setRotation,
      applyRotation,
      setScale,
      applyScale,
      applyStepScale,
      applyFilter,
      applyBorderType,
      applyOrientation,
      calculateNewScaleAndOffsets,
      calculateNewRotation,
    };
  });
